import {
  lazy,
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  loadProductDetail,
  loadProductCatalog,
  type Locale,
  type PriceQuote,
  type ProductCatalogIndex,
  type ProductCatalogIndexItem,
  type ProductCatalogItem,
} from "@/catalog";
import { itemsForCountry } from "@/catalog-view";
import {
  CheckoutPriceChangedError,
  checkoutStateAfterPageRestore,
  listenForCheckoutPageRestore,
  requestCheckoutUrlAtQuote,
  type CheckoutUiState,
} from "@/checkout-client";
import { CompanyLogo } from "@/components/CompanyLogo";
import { copy } from "@/copy";
import DetailPage from "@/detail/DetailPage";
import { companyPath, formatInteger, formatMoney, sourceStatusToken } from "@/format";
import { recordProductEventWithoutBlocking } from "@/product-events-client";
import { FREE_SAMPLE_SLUG } from "@/product-scope";
import {
  isMarketCurrencyResponse,
  opportunityCurrencyOptions,
  type OpportunityCurrency,
} from "@/pricing";

const CompanyMap = lazy(() => import("@/components/CompanyMap"));
const AccessPage = lazy(() => import("@/components/AccessPage"));

type ViewMode = "list" | "map";
type SortMode = "most-unlocked" | "highest-price" | "newest" | "az";

const PRICE_POLL_MS = 60_000;
const PRICE_RETRY_MS = 5_000;

function localeFromPath(): Locale {
  return window.location.pathname === "/pt" || window.location.pathname.startsWith("/pt/") ? "pt" : "en";
}

function slugFromPath(): string | null {
  return /^\/(?:en|pt)\/company\/([a-z0-9-]+)\/?$/.exec(window.location.pathname)?.[1] ?? null;
}

function accessFromPath(): boolean {
  return /^\/(?:en|pt)\/access\/?$/.test(window.location.pathname);
}

function normalizeSearch(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function BrandMark() {
  return <span className="brand-mark" aria-hidden="true"><span /><span /><span /></span>;
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10.5 10.5 13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 4.5h10M3 8h10M3 11.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.5 4.2 6 2.75 10 4.2 13.5 2.75v9.05L10 13.25 6 11.8 2.5 13.25V4.2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M6 2.75v9.05M10 4.2v9.05" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function Shell({ locale, onNavigate, children }: { locale: Locale; onNavigate: (path: string) => void; children: ReactNode }) {
  const t = copy[locale];
  const other: Locale = locale === "en" ? "pt" : "en";
  const currentSlug = slugFromPath();
  const currentAccess = accessFromPath();
  return <>
    <a className="skip-link" href="#main-content">Skip to content</a>
    <header className="site-header">
      <button className="brand" type="button" onClick={() => onNavigate(`/${locale}/`)} aria-label="Re:Found home"><BrandMark /><span>RE:FOUND</span></button>
      <div className="header-actions">
        <button className="header-access" type="button" onClick={() => onNavigate(`/${locale}/access`)}>{t.navAccess}</button>
        <button className="language-switch" type="button" aria-label={t.langLabel} onClick={() => onNavigate(currentSlug ? companyPath(other, currentSlug) : currentAccess ? `/${other}/access` : `/${other}/`)}>{t.lang}</button>
      </div>
    </header>
    {children}
    <footer className="site-footer">
      <div className="footer-brand"><BrandMark /><span>RE:FOUND</span></div><p>{t.integrity}</p>
      <nav aria-label="Footer"><a href="/source-policy.txt">{t.sourcePolicy}</a><a href={`/${locale}/access`}>{t.navAccess}</a></nav><small>{t.localStatus}</small>
    </footer>
  </>;
}

export function OpportunityLedgerRow({
  item,
  locale,
  rank,
  quote,
  currency,
  priceState,
  actionState,
  onOpen,
  onAction,
}: {
  item: ProductCatalogIndexItem;
  locale: Locale;
  rank: number;
  quote?: PriceQuote;
  currency: OpportunityCurrency | null;
  priceState: "loading" | "ready" | "error" | "idle";
  actionState: CheckoutUiState;
  stepCents: number;
  onOpen: (item: ProductCatalogIndexItem) => void;
  onAction: (item: ProductCatalogIndexItem) => void;
}) {
  const t = copy[locale];
  const reviewed = item.editorialStatus === "reviewed" && Boolean(item.opportunity);
  const free = reviewed && item.opportunity?.freeSample === true;
  const paid = reviewed && !free && item.opportunity?.saleable === true;
  const actionPrice = paid && quote && currency
    ? formatMoney(quote.currencyOptions[currency].amountCents, locale, currency)
    : free
      ? (locale === "pt" ? "Grátis" : "Free")
      : null;
  const actionVerb = paid ? t.unlockDossier : free ? t.openFree : t.viewBrief;
  const actionLabel = paid && actionPrice
    ? `${actionVerb} · ${actionPrice}`
    : `${actionPrice ? `${actionPrice} · ` : ""}${actionVerb}`;
  const priceBusy = paid && (priceState !== "ready" || !currency);
  const thesis = item.opportunity?.title[locale] ?? item.sourceOneLiner ?? t.noCurationBody;
  return <article className={`opportunity-row${paid ? " is-paid" : free ? " is-free" : " is-draft"}`}>
    <button type="button" className="opportunity-row-main" onClick={() => onOpen(item)} aria-label={`${t.openBrief}: ${item.name}`}>
      <CompanyLogo item={item} />
      <div className="opportunity-copy">
        <span className="opportunity-name"><span className="opportunity-rank" aria-label={`#${rank}`}>#{String(rank).padStart(2, "0")}</span> {item.name}</span>
        <div className="opportunity-row-meta"><span>YC · {item.batch ?? "—"}</span><span>{sourceStatusToken(item.reportedStatus, locale)}</span><span>{item.primaryCategory ?? "—"}</span></div>
        <span className="opportunity-intel">{thesis}</span>
      </div>
    </button>
    <div className="opportunity-row-action">
      {free && <div className="opportunity-price is-free">{locale === "pt" ? "Grátis" : "Free"}</div>}
      <button
        type="button"
        className={`button ${paid ? "button-accent" : "button-quiet"}`}
        disabled={actionState === "opening" || priceBusy}
        onClick={() => onAction(item)}
        aria-label={`${actionLabel}: ${item.name}`}
        aria-busy={actionState === "opening" || priceBusy}
      >{paid ? actionLabel : actionVerb}</button>
      {paid && quote && <small className="opportunity-unlock-count">{quote.confirmedPurchases} {t.unlocks}</small>}
      {free && <small>{locale === "pt" ? "Dossiê completo aberto" : "Complete dossier open"}</small>}
      {!reviewed && <small>{locale === "pt" ? "Briefing público · ainda não está à venda" : "Public brief · not for sale yet"}</small>}
      {actionState === "opening" && <small className="opportunity-action-status">{t.checkoutOpening}</small>}
      {paid && priceState === "error" && <small className="opportunity-action-status">{t.priceRefreshing}</small>}
      {actionState === "error" && <small role="alert">{t.checkoutError}</small>}
      {actionState === "cancelled" && <small className="opportunity-action-status" role="status">{t.checkoutCancelled}</small>}
      {actionState === "returned" && <small className="opportunity-action-status" role="status">{t.checkoutReturned}</small>}
    </div>
  </article>;
}

function FeaturedDossierCard({
  item,
  locale,
  archiveCount,
  onNavigate,
}: {
  item: ProductCatalogIndexItem;
  locale: Locale;
  archiveCount: number;
  onNavigate: (path: string) => void;
}) {
  const t = copy[locale];
  const opportunity = item.opportunity;
  if (!opportunity?.freeSample) return null;
  const sourceCount = opportunity.researchSourceCount ?? 0;
  const inventorySplit = locale === "pt"
    ? `1 gratuito + ${formatInteger(archiveCount, locale)} dossiês pagos`
    : `1 free + ${formatInteger(archiveCount, locale)} paid dossiers`;
  const openDossier = () => {
    recordProductEventWithoutBlocking("opportunity_opened", item.slug, locale, "home");
    onNavigate(companyPath(locale, item.slug));
  };
  return (
    <article className="featured-dossier" aria-labelledby="featured-dossier-title">
      <div className="featured-dossier-copy">
        <div className="featured-dossier-flags">
          <strong>{t.featuredBadge}</strong>
          <span>{inventorySplit}</span>
        </div>
        <div className="featured-dossier-company">
          <CompanyLogo item={item} />
          <p><strong>{item.name}</strong><span>{t.featuredCategory}</span></p>
        </div>
        <h2 id="featured-dossier-title">{opportunity.title[locale]}</h2>
        <p className="featured-dossier-summary">{opportunity.teaser?.[locale]}</p>
        <div className="featured-dossier-facts" aria-label={locale === "pt" ? "Conteúdo incluído" : "Included content"}>
          <span><strong>{sourceCount}</strong> {t.featuredSources}</span>
          <span><strong>5</strong> {locale === "pt" ? "fases" : "phases"} · {t.featuredPlan}</span>
        </div>
        <button type="button" className="button button-accent featured-dossier-cta" onClick={openDossier}>
          {t.featuredOpen} <span aria-hidden="true">→</span>
        </button>
      </div>
      <div className="decision-tape" aria-label={t.featuredSystem}>
        <p className="eyebrow">{t.featuredSystem}</p>
        <ol>
          {t.featuredTape.map((label, index) => (
            <li key={label}><span>{String(index + 1).padStart(2, "0")}</span><strong>{label}</strong></li>
          ))}
        </ol>
        <p>{t.featuredSystemBody}</p>
      </div>
    </article>
  );
}

function HomePage({ locale, catalog, fallback, currency, onNavigate }: { locale: Locale; catalog: ProductCatalogIndex; fallback: boolean; currency: OpportunityCurrency | null; onNavigate: (path: string) => void }) {
  const t = copy[locale];
  const priceRise = currency
    ? `${t.priceRises} ${formatMoney(
        opportunityCurrencyOptions(catalog.pricing.stepCents)[currency].amountCents,
        locale,
        currency,
      )}.`
    : t.priceLoading;
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [category, setCategory] = useState("all");
  const [country, setCountry] = useState("all");
  const [sort, setSort] = useState<SortMode>("most-unlocked");
  const [view, setView] = useState<ViewMode>(() => window.location.hash === "#map" ? "map" : "list");
  const [visible, setVisible] = useState(36);
  const featuredItem = useMemo(
    () => catalog.items.find((item) => item.slug === FREE_SAMPLE_SLUG && item.opportunity?.freeSample === true),
    [catalog.items],
  );
  const archiveItems = useMemo(
    () => catalog.items.filter((item) => item.slug !== FREE_SAMPLE_SLUG),
    [catalog.items],
  );
  const reviewed = useMemo(() => archiveItems.filter((item) =>
    item.editorialStatus === "reviewed"
    && item.opportunity?.status === "reviewed"
    && item.opportunity.saleable === true), [archiveItems]);
  const [quoteBySlug, setQuoteBySlug] = useState<Record<string, PriceQuote>>({});
  const [quoteErrors, setQuoteErrors] = useState<Record<string, boolean>>({});
  const [checkoutResult, setCheckoutResult] = useState<{ slug: string; state: CheckoutUiState } | null>(null);
  const checkoutInFlightRef = useRef<string | null>(null);
  const filterDialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const syncHash = () => setView(window.location.hash === "#map" ? "map" : "list");
    window.addEventListener("refound:navigate", syncHash);
    return () => window.removeEventListener("refound:navigate", syncHash);
  }, []);

  useEffect(() => listenForCheckoutPageRestore(() => {
    checkoutInFlightRef.current = null;
    setCheckoutResult((current) => current
      ? { ...current, state: checkoutStateAfterPageRestore(current.state) }
      : current);
  }), []);

  const filtered = useMemo(() => {
    const needle = normalizeSearch(deferredQuery.trim());
    return archiveItems.filter((item) => {
      if (category !== "all" && item.primaryCategory !== category) return false;
      if (!needle) return true;
      return normalizeSearch([
        item.name,
        item.sourceOneLiner,
        item.primaryCategory,
        item.opportunity?.title.en,
        item.opportunity?.title.pt,
        item.opportunity?.teaser?.en,
        item.opportunity?.teaser?.pt,
        item.searchText,
      ].filter(Boolean).join(" ")).includes(needle);
    });
  }, [archiveItems, category, deferredQuery]);
  const ranked = useMemo(() => [...filtered].sort((left, right) => {
    const leftQuote = quoteBySlug[left.slug];
    const rightQuote = quoteBySlug[right.slug];
    const reviewedOrder = Number(right.editorialStatus === "reviewed") - Number(left.editorialStatus === "reviewed");
    if (sort === "most-unlocked") {
      return (rightQuote?.confirmedPurchases ?? -1) - (leftQuote?.confirmedPurchases ?? -1)
        || (rightQuote?.priceCents ?? -1) - (leftQuote?.priceCents ?? -1)
        || reviewedOrder
        || left.name.localeCompare(right.name);
    }
    if (sort === "highest-price") {
      return (rightQuote?.priceCents ?? -1) - (leftQuote?.priceCents ?? -1)
        || (rightQuote?.confirmedPurchases ?? -1) - (leftQuote?.confirmedPurchases ?? -1)
        || reviewedOrder
        || left.name.localeCompare(right.name);
    }
    if (sort === "newest") {
      return (right.batch ?? "").localeCompare(left.batch ?? "") || left.name.localeCompare(right.name);
    }
    return left.name.localeCompare(right.name);
  }), [filtered, quoteBySlug, sort]);
  const mapResults = useMemo(() => itemsForCountry(ranked, country), [country, ranked]);
  const listResults = country === "all" ? ranked : mapResults;
  const selectedCountryName = country === "all" ? null : new Intl.DisplayNames(
    [locale === "pt" ? "pt-BR" : "en"],
    { type: "region" },
  ).of(country);
  const categoryOptions = useMemo(() => [...new Set(archiveItems.map((item) => item.primaryCategory).filter((value): value is string => Boolean(value)))].sort((left, right) => left.localeCompare(right)), [archiveItems]);
  const countryOptions = useMemo(() => {
    const names = new Intl.DisplayNames([locale === "pt" ? "pt-BR" : "en"], { type: "region" });
    return [...new Set(archiveItems.flatMap((item) => item.countryCodes ?? []))]
      .map((code) => ({ code, name: names.of(code) ?? code }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [archiveItems, locale]);
  useEffect(() => {
    const sellable = reviewed.filter((item) => !item.opportunity?.freeSample);
    if (sellable.length === 0) return;
    const controller = new AbortController();
    let refreshTimer: number | null = null;
    let inFlight = false;
    const stopPolling = () => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      refreshTimer = null;
    };
    const schedule = (delayMs: number) => {
      stopPolling();
      if (controller.signal.aborted || document.visibilityState === "hidden") return;
      refreshTimer = window.setTimeout(() => void loadPrices(), delayMs);
    };
    const loadPrices = async (): Promise<void> => {
      if (inFlight || controller.signal.aborted || document.visibilityState === "hidden") return;
      inFlight = true;
      let nextDelay = PRICE_RETRY_MS;
      try {
        const response = await fetch("/api/prices", { signal: controller.signal });
        if (!response.ok) throw new Error("prices_unavailable");
        const body = (await response.json()) as { quotes?: Record<string, PriceQuote> };
        if (!body.quotes || typeof body.quotes !== "object") throw new Error("invalid_prices");
        if (controller.signal.aborted) return;
        setQuoteBySlug(body.quotes);
        setQuoteErrors({});
        nextDelay = PRICE_POLL_MS;
      } catch {
        if (controller.signal.aborted) return;
        setQuoteErrors(Object.fromEntries(sellable.map(({ slug }) => [slug, true])));
      } finally {
        inFlight = false;
        schedule(nextDelay);
      }
    };
    const startPolling = () => {
      stopPolling();
      if (document.visibilityState === "hidden") return;
      void loadPrices();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") stopPolling();
      else startPolling();
    };
    startPolling();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      controller.abort();
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [reviewed]);

  const changeCountry = (nextCountry: string) => setCountry(nextCountry);
  const switchView = (next: ViewMode) => {
    setView(next);
    const url = new URL(window.location.href);
    url.hash = next === "map" ? "map" : "explore";
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  };
  const startCheckout = async (target: ProductCatalogIndexItem) => {
    if (!target.opportunity || target.opportunity.status === "draft" || target.opportunity.freeSample || target.opportunity.saleable !== true) {
      onNavigate(companyPath(locale, target.slug));
      return;
    }
    if (!currency || !quoteBySlug[target.slug] || quoteErrors[target.slug]) return;
    if (checkoutInFlightRef.current) return;
    checkoutInFlightRef.current = target.slug;
    setCheckoutResult({ slug: target.slug, state: "opening" });
    try {
      const quote = quoteBySlug[target.slug];
      if (!quote || !Number.isSafeInteger(quote.slotIndex)) throw new Error("price_unavailable");
      window.location.assign(await requestCheckoutUrlAtQuote(locale, target.slug, {
        priceCents: quote.priceCents,
        slotIndex: Number(quote.slotIndex),
      }));
    } catch (error) {
      checkoutInFlightRef.current = null;
      if (error instanceof CheckoutPriceChangedError) {
        setQuoteBySlug((current) => ({ ...current, [target.slug]: error.quote }));
        setQuoteErrors((current) => ({ ...current, [target.slug]: false }));
        setCheckoutResult(null);
        return;
      }
      setCheckoutResult({ slug: target.slug, state: "error" });
    }
  };
  const activeFilterCount = Number(category !== "all")
    + Number(country !== "all")
    + Number(sort !== "most-unlocked");
  const clearAllFilters = () => {
    setQuery("");
    setCategory("all");
    setCountry("all");
    setSort("most-unlocked");
  };
  const companyCountLabel = formatInteger(catalog.counts.companies, locale);
  const archiveCountLabel = formatInteger(archiveItems.length, locale);
  const archiveEyebrow = locale === "pt"
    ? `${companyCountLabel} OPORTUNIDADES COM DOSSIÊ DA Y COMBINATOR`
    : `${companyCountLabel} Y COMBINATOR OPPORTUNITIES WITH DOSSIERS`;
  const inventoryComplete = locale === "pt"
    ? `${archiveCountLabel} / ${archiveCountLabel} dossiês pagos no arquivo pesquisável`
    : `${archiveCountLabel} / ${archiveCountLabel} paid dossiers in the searchable archive`;

  if (fallback) {
    return <main id="main-content" className="archive-main unavailable-main">
      <section className="load-failure" role="alert">
        <p className="eyebrow">{locale === "pt" ? "CATÁLOGO INDISPONÍVEL" : "CATALOG UNAVAILABLE"}</p>
        <h1>{locale === "pt" ? "Não vamos mostrar conteúdo antigo ou sem revisão." : "We will not show stale or unreviewed content."}</h1>
        <p>{t.fallback}</p>
        <button type="button" className="button button-accent" onClick={() => window.location.reload()}>
          {locale === "pt" ? "Tentar novamente" : "Try again"} →
        </button>
      </section>
    </main>;
  }

  return <main id="main-content" className="archive-main">
    <section id="explore" className="archive-board" aria-labelledby="archive-heading">
      <header className="archive-heading">
        <p className="eyebrow">{archiveEyebrow}</p>
        <h1 id="archive-heading">{t.archiveTitle}</h1>
        <p>{t.archiveBody}</p>
      </header>
      {featuredItem ? <FeaturedDossierCard item={featuredItem} locale={locale} archiveCount={archiveItems.length} onNavigate={onNavigate} /> : null}
      <div className="archive-controls">
        <label className="archive-search"><SearchIcon /><span className="sr-only">{t.searchLabel}</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t.searchPlaceholder} /></label>
        <label className="archive-select desktop-filters"><span>{t.category}</span><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="all">{t.anyCategory}</option>{categoryOptions.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
        <label className="archive-select desktop-filters"><span>{t.country}</span><select value={country} onChange={(event) => changeCountry(event.target.value)}><option value="all">{t.anyCountry}</option>{countryOptions.map((value) => <option key={value.code} value={value.code}>{value.name}</option>)}</select></label>
        <label className="archive-select desktop-filters"><span>{t.sort}</span><select value={sort} onChange={(event) => setSort(event.target.value as SortMode)}><option value="most-unlocked">{t.recommended}</option><option value="highest-price">{t.highestPrice}</option><option value="newest">{t.newest}</option><option value="az">{t.az}</option></select></label>
        <button type="button" className="mobile-filter-trigger" aria-haspopup="dialog" aria-controls="mobile-filters" onClick={() => filterDialogRef.current?.showModal()}>{t.filters}{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ""}</button>
        <div className="view-switch archive-view-switch">
          <button type="button" aria-pressed={view === "list"} onClick={() => switchView("list")}><ListIcon /> {t.list}</button>
          <button type="button" aria-pressed={view === "map"} onClick={() => switchView("map")}><MapIcon /> {t.map}</button>
        </div>
      </div>
      <dialog id="mobile-filters" ref={filterDialogRef} className="filter-dialog" aria-labelledby="filter-dialog-title">
        <form method="dialog" className="filter-dialog-panel">
          <header><div><p className="eyebrow">{t.filters}</p><h2 id="filter-dialog-title">{t.filtersTitle}</h2><p>{t.filtersBody}</p></div><button type="submit" className="filter-dialog-close" aria-label={locale === "pt" ? "Fechar filtros" : "Close filters"}>×</button></header>
          <label className="archive-select"><span>{t.category}</span><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="all">{t.anyCategory}</option>{categoryOptions.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          <label className="archive-select"><span>{t.country}</span><select value={country} onChange={(event) => changeCountry(event.target.value)}><option value="all">{t.anyCountry}</option>{countryOptions.map((value) => <option key={value.code} value={value.code}>{value.name}</option>)}</select></label>
          <label className="archive-select"><span>{t.sort}</span><select value={sort} onChange={(event) => setSort(event.target.value as SortMode)}><option value="most-unlocked">{t.recommended}</option><option value="highest-price">{t.highestPrice}</option><option value="newest">{t.newest}</option><option value="az">{t.az}</option></select></label>
          <footer><button type="button" className="button button-quiet" onClick={clearAllFilters}>{t.clearAll}</button><button type="submit" className="button button-accent">{t.applyFilters} · {formatInteger(listResults.length, locale)}</button></footer>
        </form>
      </dialog>
      <div className={`discovery-stage view-${view}`}>
        <div className="discovery-canvas">
          {(view === "list" ? listResults.length : ranked.length) === 0 ? <div className="empty-state"><h3>{t.noResults}</h3><button type="button" className="button button-quiet" onClick={clearAllFilters}>{t.clearFilters}</button></div>
            : view === "map" ? <div id="map"><Suspense fallback={<div className="map-loading">{t.mapLoading}</div>}><CompanyMap items={ranked} resultItems={mapResults} locale={locale} selectedCountry={country} onCountryChange={changeCountry} onOpen={(target) => onNavigate(companyPath(locale, target.slug))} onShowList={() => { setVisible(Math.max(36, Math.min(mapResults.length, 96))); switchView("list"); }} /></Suspense></div>
              : <>{country !== "all" && <div className="active-country-filter"><span>{t.countrySelection}</span><strong>{selectedCountryName ?? country} · {formatInteger(listResults.length, locale)}</strong><button type="button" onClick={() => setCountry("all")}>{t.clearCountry} ×</button></div>}<div className="opportunity-list" role="feed">{listResults.slice(0, visible).map((item, index) => <OpportunityLedgerRow key={item.slug} item={item} locale={locale} rank={index + 1} quote={quoteBySlug[item.slug]} currency={currency} priceState={quoteErrors[item.slug] ? "error" : quoteBySlug[item.slug] && currency ? "ready" : item.editorialStatus === "reviewed" && item.opportunity?.saleable === true && !item.opportunity?.freeSample ? "loading" : "idle"} actionState={checkoutResult?.slug === item.slug ? checkoutResult.state : "idle"} stepCents={catalog.pricing.stepCents} onOpen={(target) => onNavigate(companyPath(locale, target.slug))} onAction={(target) => void startCheckout(target)} />)}</div>{visible < listResults.length && <button type="button" className="button load-more" onClick={() => setVisible((value) => value + 36)}>{t.loadMore} · {formatInteger(listResults.length - visible, locale)}</button>}</>}
        </div>
      </div>
        <div className="archive-proof"><span><strong>{formatInteger(country !== "all" ? listResults.length : ranked.length, locale)}</strong> {t.results}</span><span>{catalog.coverage.fullInventoryCovered ? inventoryComplete : "PARTIAL INVENTORY"}</span><span>{priceRise}</span></div>
    </section>
  </main>;
}

function DetailRoute({ slug, locale, currency, onNavigate }: { slug: string; locale: Locale; currency: OpportunityCurrency | null; onNavigate: (path: string) => void }) {
  const [state, setState] = useState<{ item: ProductCatalogItem | null; error: boolean }>({
    item: null,
    error: false,
  });
  useEffect(() => {
    const controller = new AbortController();
    loadProductDetail(slug, controller.signal)
      .then((item) => setState({ item, error: false }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setState({ item: null, error: true });
      });
    return () => controller.abort();
  }, [slug]);
  if (state.item) {
    return <DetailPage key={state.item.slug} item={state.item} locale={locale} currency={currency} onNavigate={onNavigate} />;
  }
  if (!state.error) return <div className="app-loading" role="status"><BrandMark /><span>RE:FOUND</span></div>;
  return <main id="main-content" className="detail-main unavailable-main">
    <section className="load-failure" role="alert">
      <p className="eyebrow">{locale === "pt" ? "DOSSIÊ INDISPONÍVEL" : "DOSSIER UNAVAILABLE"}</p>
      <h1>{locale === "pt" ? "Não vamos substituir a análise revisada por um rascunho." : "We will not replace reviewed analysis with a draft."}</h1>
      <p>{locale === "pt" ? "O catálogo local não respondeu ou entregou uma versão antiga. Inicie o Worker e tente novamente." : "The local catalog did not respond or returned a stale version. Start the Worker and try again."}</p>
      <div className="load-failure-actions">
        <button type="button" className="button button-accent" onClick={() => window.location.reload()}>{locale === "pt" ? "Tentar novamente" : "Try again"} →</button>
        <button type="button" className="button button-quiet" onClick={() => onNavigate(`/${locale}/#explore`)}>← {locale === "pt" ? "Voltar ao arquivo" : "Back to archive"}</button>
      </div>
    </section>
  </main>;
}

export default function App() {
  const [locale, setLocale] = useState<Locale>(localeFromPath);
  const [slug, setSlug] = useState<string | null>(slugFromPath);
  const [access, setAccess] = useState(accessFromPath);
  const [catalogState, setCatalogState] = useState<{ catalog: ProductCatalogIndex; fallback: boolean } | null>(null);
  const [marketCurrency, setMarketCurrency] = useState<OpportunityCurrency | null>(null);
  useEffect(() => { const controller = new AbortController(); loadProductCatalog(controller.signal).then(setCatalogState).catch(() => undefined); return () => controller.abort(); }, []);
  useEffect(() => {
    const controller = new AbortController();
    let retryTimer: number | null = null;
    const loadMarket = async (): Promise<void> => {
      try {
        const response = await fetch("/api/market", { cache: "no-store", signal: controller.signal });
        if (!response.ok) throw new Error("market_unavailable");
        const body: unknown = await response.json();
        if (!isMarketCurrencyResponse(body)) throw new Error("market_invalid");
        setMarketCurrency(body.currency);
      } catch (error: unknown) {
        if (controller.signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
        retryTimer = window.setTimeout(() => void loadMarket(), PRICE_RETRY_MS);
      }
    };
    void loadMarket();
    return () => {
      controller.abort();
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, []);
  useEffect(() => { const onPopState = () => { setLocale(localeFromPath()); setSlug(slugFromPath()); setAccess(accessFromPath()); }; window.addEventListener("popstate", onPopState); return () => window.removeEventListener("popstate", onPopState); }, []);
  useEffect(() => { const onKey = (event: KeyboardEvent) => { if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); window.location.hash = "explore"; window.setTimeout(() => document.querySelector<HTMLInputElement>(".archive-search input")?.focus(), 50); } }; window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, []);
  const navigate = useCallback((path: string) => {
    const [pathname, hash = ""] = path.split("#"); window.history.pushState({}, "", `${pathname}${hash ? `#${hash}` : ""}`);
    setLocale(localeFromPath()); setSlug(slugFromPath()); setAccess(accessFromPath());
    window.dispatchEvent(new Event("refound:navigate"));
    if (hash) window.setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 0); else window.scrollTo({ top: 0, behavior: "instant" });
  }, []);
  if (!catalogState) return <div className="app-loading"><BrandMark /><span>RE:FOUND</span></div>;
  return <Shell locale={locale} onNavigate={navigate}>{access ? <Suspense fallback={<div className="app-loading"><BrandMark /><span>RE:FOUND</span></div>}><AccessPage locale={locale} onNavigate={navigate} /></Suspense> : slug ? <DetailRoute key={slug} slug={slug} locale={locale} currency={marketCurrency} onNavigate={navigate} /> : <HomePage locale={locale} catalog={catalogState.catalog} fallback={catalogState.fallback} currency={marketCurrency} onNavigate={navigate} />}</Shell>;
}
